import React, { useState, Fragment } from 'react'
import { FormattedMessage } from 'react-intl'
import { css } from '@emotion/css'
import { ConfigurationPanel } from '../../legacy-design-system/configuration-panel'
import { Role, hasRole } from '../roles'
import { SensitiveDataMaskingSettingsLoader } from './sensitive-data-masking-settings-loader'
import Spinner from 'react-spinkit'
import { theme } from '@contrast-security-inc/design-system-foundations'
import { Checkbox } from '../../legacy-design-system/checkbox'
import {
  UpdateSensitiveDataMaskingSettingsMutation,
  UpdateSensitiveDataMaskingSettingsArgs
} from './update-sensitive-data-masking-settings-mutation'
import { sendDefaultEvent } from '../analytics'
import { ContrastFormattedMessage } from '../contrast-formatted-message'
import {
  getRbacStatus,
  userHasResourceAction
} from 'contrastBase/accessControl'
import { PanelSubtitle } from '../../legacy-design-system/configuration-panel/configuration-panel'
import { Button } from '@contrast-security-inc/design-system-buttons'

export function SensitiveDataMaskingSettingsForm({
  organizationID,
  organizationRole,
  loadingRole
}: {
  readonly organizationID: string
  readonly organizationRole: Role
  readonly loadingRole: boolean
}) {
  return (
    <div>
      <SensitiveDataMaskingSettingsLoader organizationID={organizationID}>
        {({ loading, maskAttackVector, maskHttpBody }) => {
          return (
            <UpdateSensitiveDataMaskingSettingsMutation>
              {(updateSensitiveDataMaskingSettingsMutation) => {
                return (
                  <SensitiveDataMaskingSettingsFormView
                    organizationID={organizationID}
                    organizationRole={organizationRole}
                    initialMaskHttpBody={maskHttpBody}
                    initialMaskAttackVector={maskAttackVector}
                    updateSensitiveDataMaskingSettingsMutation={
                      updateSensitiveDataMaskingSettingsMutation
                    }
                    loading={loading || loadingRole}
                  />
                )
              }}
            </UpdateSensitiveDataMaskingSettingsMutation>
          )
        }}
      </SensitiveDataMaskingSettingsLoader>
    </div>
  )
}

export interface SensitiveDataMaskingSettingsFormViewProps {
  readonly organizationID: string
  readonly organizationRole: Role
  readonly initialMaskHttpBody: boolean
  readonly initialMaskAttackVector: boolean
  readonly loading: boolean
  readonly updateSensitiveDataMaskingSettingsMutation: (
    variables: UpdateSensitiveDataMaskingSettingsArgs
  ) => Promise<any>
}

export function SensitiveDataMaskingSettingsFormView({
  organizationID,
  organizationRole,
  initialMaskHttpBody,
  initialMaskAttackVector,
  updateSensitiveDataMaskingSettingsMutation,
  loading
}: SensitiveDataMaskingSettingsFormViewProps) {
  return (
    <ConfigurationPanel
      title="title_sensitiveData_sensitiveDataMasking"
      subtitle={
        <PanelSubtitle
          subtitleI18n={'detail_sensitiveDataMasking_description'}
          linkI18n={'detail_sensitiveDataMasking_description_link'}
        />
      }
      children={
        loading ? (
          <SensitiveDataLoading />
        ) : (
          <SensitiveDataMaskingSettingsFormViewContent
            organizationID={organizationID}
            organizationRole={organizationRole}
            initialMaskHttpBody={initialMaskHttpBody}
            initialMaskAttackVector={initialMaskAttackVector}
            updateSensitiveDataMaskingSettingsMutation={
              updateSensitiveDataMaskingSettingsMutation
            }
          />
        )
      }
    />
  )
}

export const sensitiveDataMaskingLoadingIconTestId =
  'loadingIcon-sensitiveDataMasking'
export const checkboxMaskHttpBodyTestId = 'checkbox-maskHttpBody'
export const checkboxMaskAttackVectorTestId = 'checkbox-maskAttackVector'
export const buttonSaveSensitiveDataSettingsTestId =
  'button-saveSensitiveDataSettingsTestId'

export function SensitiveDataMaskingSettingsFormViewContent({
  organizationID,
  organizationRole,
  initialMaskHttpBody,
  initialMaskAttackVector,
  updateSensitiveDataMaskingSettingsMutation
}: {
  readonly organizationID: string
  readonly organizationRole: Role
  readonly initialMaskHttpBody: boolean
  readonly initialMaskAttackVector: boolean
  readonly updateSensitiveDataMaskingSettingsMutation: (
    variables: UpdateSensitiveDataMaskingSettingsArgs
  ) => Promise<any>
}) {
  const [maskHttpBody, setMaskHttpBody] = useState(initialMaskHttpBody)
  const [maskAttackVector, setMaskAttackVector] = useState(
    initialMaskAttackVector
  )
  const [formHasChanged, setFormHasChanged] = React.useState(false)

  const { isAssessEnforced } = getRbacStatus()
  const isEditable = isAssessEnforced
    ? userHasResourceAction(
        'SENSITIVE_DATA',
        'PROTECT_SENSITIVE_DATA_POLICY_MANAGE'
      )
    : hasRole(organizationRole, 'RULES_ADMIN')

  return (
    <div
      className={css`
        padding: 15px 15px 15px 15px;
      `}
    >
      <div
        className={css`
          padding-bottom: 10px;
        `}
      >
        <FormattedMessage id="detail_sensitiveData_maskEntireBody_explanation" />
      </div>

      <div
        className={css`
          padding-bottom: 10px;
        `}
      >
        <ContrastFormattedMessage id="detail_sensitiveData_maskEntireBody">
          {(message) => {
            return (
              <Checkbox
                data-testid={checkboxMaskHttpBodyTestId}
                label={message}
                value={maskHttpBody as boolean}
                onChange={() => {
                  setFormHasChanged(true)
                  setMaskHttpBody(!maskHttpBody)
                }}
                disabled={!isEditable}
                renderHelpContent={() => {
                  return (
                    <div
                      className={css`
                        width: 250px;
                      `}
                    >
                      <FormattedMessage id="detail_sensitiveData_maskEntireBody_tooltip" />
                    </div>
                  )
                }}
              />
            )
          }}
        </ContrastFormattedMessage>
      </div>

      <div
        className={css`
          padding-bottom: 10px;
        `}
      >
        <FormattedMessage id="detail_sensitiveData_maskAttackVector_explanation" />
      </div>

      <ContrastFormattedMessage id="detail_sensitiveData_maskAttackVector">
        {(message) => {
          return (
            <Checkbox
              data-testid={checkboxMaskAttackVectorTestId}
              label={message}
              value={maskAttackVector as boolean}
              onChange={() => {
                setFormHasChanged(true)
                setMaskAttackVector(!maskAttackVector)
              }}
              disabled={!isEditable}
              renderHelpContent={() => {
                return (
                  <div
                    className={css`
                      width: 250px;
                    `}
                  >
                    <FormattedMessage id="detail_sensitiveData_maskAttackVector_tooltip" />
                  </div>
                )
              }}
            />
          )
        }}
      </ContrastFormattedMessage>

      <div
        className={css`
          display: flex;
          flex-direction: row-reverse;
        `}
      >
        <FormattedMessage
          id={'action_save'}
          children={(saveMessage) => (
            <Fragment>
              <Button
                data-testid={buttonSaveSensitiveDataSettingsTestId}
                className={css`
                  margin-right: 20px;
                `}
                onClick={async () => {
                  await updateSensitiveDataMaskingSettingsMutation({
                    maskAttackVector,
                    maskHttpBody,
                    organizationID
                  })
                    .then(() => {
                      setFormHasChanged(false)
                      sendDefaultEvent(
                        maskHttpBody
                          ? 'sensitive.data.mask.httpbody.enable'
                          : 'sensitive.data.mask.httpbody.disable'
                      )
                      sendDefaultEvent(
                        maskAttackVector
                          ? 'sensitive.data.mask.vector.enable'
                          : 'sensitive.data.mask.vector.disable'
                      )
                    })
                    .catch(() => {
                      setFormHasChanged(true)
                    })
                }}
                isDisabled={!isEditable || !formHasChanged}
              >
                {saveMessage}
              </Button>
            </Fragment>
          )}
        />
      </div>
    </div>
  )
}

export function SensitiveDataLoading() {
  return (
    <div
      className={css`
        height: 77px;
        display: flex;
        justify-content: center;
        align-items: center;
      `}
      data-testid={sensitiveDataMaskingLoadingIconTestId}
    >
      <Spinner name="ball-beat" fadeIn="none" color={theme.color.teal50} />
    </div>
  )
}
